import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, DateRangeInterface, DSData, DSSetup, Service } from 'webapp-types';
import {
	DataSelectType,
	FakeModelIdPrefix,
	ForecastMethod,
	ForecastStatusType,
} from 'webapp/enums/enums';
import { copyObject, uniqueId } from 'webapp/utils/common';

import type { PreviewData } from 'webapp/components/forecasts/data/index';

import type Block from 'webapp/models-custom/block';
import type { ForecastMethodology } from 'webapp/models-custom/forecast';
import MetricSetup from 'webapp/models-custom/metric-setup';
import Serviced from 'webapp/models-custom/serviced';

export const DEFAULT_CORRELATION = 0.5;

export interface RegressorMetricData {
	id?: string | null;
	forecastId?: string;
	metricSetupKey: string;
	metricSetupSpaceAccessId: number | null;
	metricSetupDimensions: string[];
	metricSetupFilters?: DSSetup.MetricSetupFiltersData | null;
	methodology: ForecastMethodology;
	correlation: number | null;
	enabled?: boolean;
	statusObject?: APIData.Forecast.StatusObject | null;
}

export default class RegressorMetric extends Serviced {
	@service('request') declare requestService: Service.Request;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('store') declare storeService: Service.Store;

	// ------- properties ------

	@tracked id: string | null = null;
	@tracked forecastId = '';
	@tracked metricSetupKey = '';
	@tracked metricSetupSpaceAccessId: number | null = null;
	@tracked metricSetupDimensions: string[] = [];
	@tracked metricSetupFilters: DSSetup.MetricSetupFiltersData | null = null;
	@tracked private _methodology: ForecastMethodology = {
		type: ForecastMethod.DATABOX,
		extra: null,
	};
	@tracked private _correlation: number | null = null;
	@tracked private _enabled = true;

	// ------- correlation calculation state ------

	@tracked correlationCalculationFailed = false;
	@tracked isCalculatingCorrelation = false;

	// ------- request state ------

	@tracked previewData: PreviewData | null = null;
	@tracked statusObject: APIData.Forecast.StatusObject | null = null;

	// ------- table data stuff ------

	@tracked private _tableBlock: Block | null = null;

	constructor(data: RegressorMetricData) {
		super(); // Call Serviced constructor
		this.id = data.id || `temp-${uniqueId()}`;
		this.forecastId = data.forecastId || '';
		this.metricSetupKey = data.metricSetupKey;
		this.metricSetupSpaceAccessId = data.metricSetupSpaceAccessId;
		this.metricSetupDimensions = data.metricSetupDimensions;
		this.metricSetupFilters = data.metricSetupFilters || null;
		this._methodology = data.methodology;
		this._correlation = data.correlation;
		this._enabled = data.enabled !== undefined ? data.enabled : true;
		this.statusObject = data.statusObject || null;
	}

	get methodology(): ForecastMethodology {
		return this._methodology;
	}

	set methodology(value: ForecastMethodology) {
		this._methodology = value;
	}

	get correlation(): number | null {
		return this._correlation;
	}

	set correlation(value: number | null) {
		this._correlation = value;
	}

	get enabled(): boolean {
		return this._enabled;
	}

	set enabled(value: boolean) {
		this._enabled = value;
	}

	setCalculatedCorrelation(value: number | null = null): void {
		if (value === null) {
			this.correlationCalculationFailed = true;
			this.correlation = DEFAULT_CORRELATION; // fallback to 0.5
		} else {
			this.correlation = Math.round(value * 100) / 100;
			this.correlationCalculationFailed = false;
		}
	}

	// ------- Status handling methods ------

	setStatusFromResponse(statusObject: APIData.Forecast.StatusObject | null): void {
		this.statusObject = statusObject;
	}

	clearStatus(): void {
		this.statusObject = null;
	}

	get statusObjectType(): string | undefined {
		return this.statusObject?.type;
	}

	get hasStatusError(): boolean {
		if (!this.statusObjectType) {
			return false;
		}

		return [
			ForecastStatusType.PREVIEW_GENERATION_FAILED,
			ForecastStatusType.SNAPSHOT_GENERATION_FAILED,
		].includes(this.statusObjectType as ForecastStatusType);
	}

	get statusObjectMessage(): string | undefined {
		return this.statusObject?.data?.message;
	}

	get isLoading(): boolean {
		return this.statusObjectType === ForecastStatusType.SNAPSHOT_GENERATING;
	}

	get error(): Error | null {
		if (this.hasStatusError) {
			return new Error(this.statusObjectMessage || '');
		}
		return null;
	}

	// ------- other props ------

	@cached
	get metricSetup(): MetricSetup {
		return new MetricSetup({
			key: this.metricSetupKey,
			spaceAccessId: this.metricSetupSpaceAccessId,
			dimensions: this.metricSetupDimensions,
			filters: this.metricSetupFilters,
		});
	}

	// ------- Permission and metric name helpers ------

	get isMissingMetric(): boolean {
		return !!this.metricSetupKey && !this.metricSetup?.metric;
	}

	get hasNoMetricPermission(): boolean {
		return this.isMissingMetric;
	}

	get needsConnection(): boolean {
		return (
			!!this.metricSetupKey &&
			this.metricSetupSpaceAccessId === null &&
			!!this.metricSetup?.sourceType &&
			!this.metricSetup?.metric?.isUserGeneric
		);
	}

	get hasMetricUnavailableError(): boolean {
		const metric = this.metricSetup?.metric;
		if (!metric || this.needsConnection) {
			return false;
		}

		// metric unavailable if no data points received AND metric is user generic
		// or metric is missing
		const hasNoDataPoints = !this.historicalDataSelect?.outputs?.[0]?.points?.length;
		if (
			(hasNoDataPoints && metric.isUserGeneric && this.metricSetupSpaceAccessId === null) ||
			this.isMissingMetric
		) {
			return true;
		}

		return false;
	}

	get metricNameFromData(): string | null {
		const historicalDataSelect = this.historicalDataSelect;
		return historicalDataSelect?.dataRoot?.metricName || null;
	}

	get displayMetricName(): string {
		return this.metricSetup?.selectedMetricTitle || this.metricNameFromData || 'Unknown metric';
	}

	// ------- End of permission and metric name helpers ------

	get tableBlock() {
		return this._tableBlock;
	}

	set tableBlock(block: Block | null) {
		this._tableBlock = block;
	}

	get forecastDataSelect() {
		const dataSelects = this.tableBlock?.dataSelects;
		if (!dataSelects) {
			return null;
		}
		return dataSelects.find((ds) => ds.isForecast) || null;
	}

	get historicalDataSelect() {
		const dataSelects = this.tableBlock?.dataSelects;
		if (!dataSelects) {
			return null;
		}
		return dataSelects.find((ds) => ds.isMain) || null;
	}

	get forecastWholeRangeDataSelect() {
		const dataSelects = this.tableBlock?.dataSelects;
		if (!dataSelects) {
			return null;
		}
		return dataSelects.find((ds) => ds.type === DataSelectType.FORECAST_WHOLE_RANGE) || null;
	}

	get currentValueDataSelect() {
		const dataSelects = this.tableBlock?.dataSelects;
		if (!dataSelects) {
			return null;
		}
		// find the MAIN type data select that has updateFrom set (which updates from the main historical data select)
		return (
			dataSelects.find((ds) => ds.type === DataSelectType.MAIN && ds.updateFrom !== null) ||
			null
		);
	}

	// NOTE: requestPreview has been moved to explorer component
	// Regressor previews are now handled asynchronously via Pusher events
	// The explorer component manages request lifecycle and routes events to regressor instances

	// initialize temporary block to access dataselects for display in table
	initializeTableBlock(): void {
		if (!this.previewData) {
			return;
		}

		const blockId = this.generateUUID();
		const sample = { ...this.previewData.sample };
		const sampledata = sample.sampledata;
		const dataSelects = Array.isArray(this.previewData.dataSelects)
			? [...this.previewData.dataSelects]
			: [];

		// when source is deleted, sampledata can be an empty array or missing
		if (!sampledata || Array.isArray(sampledata) || typeof sampledata !== 'object') {
			// still create the block with minimal data so UI can show the metric name
			const firstDataSelect = dataSelects[0];
			let dateRange: DateRangeInterface.FormulaInterface = {
				range: {},
				granulation: { isWholeRange: false },
			};
			if (firstDataSelect?.dateRange) {
				dateRange = copyObject(firstDataSelect.dateRange);
			}

			const block = this.mmBlocksService.createNewModel({
				id: blockId,
				dateRange: dateRange,
			});

			// create empty dataSelects for later use
			const primaryDataSelectIdNew = this.generateUUID();
			dataSelects.forEach((ds) => {
				const isPrimary = ds.id === firstDataSelect?.id;
				const dsId = isPrimary ? primaryDataSelectIdNew : this.generateUUID();

				if (!isPrimary) {
					ds.updateFrom = primaryDataSelectIdNew;
				}
				ds.id = dsId;
				ds.blockId = blockId;
			});

			// push empty dataSelects to store
			if (dataSelects.length > 0) {
				this.storeService.pushPayload({ dataSelects });
			}

			this.tableBlock = block;
			return;
		}

		const dsDataArray = sampledata?.dsData || [];
		const primaryDataSelectIdOriginal = dataSelects.find((ds) => !ds.type)?.id;
		const primaryDataSelectIdNew = this.generateUUID();

		dataSelects.forEach((ds) => {
			const isPrimary = ds.id === primaryDataSelectIdOriginal;
			const dsId = isPrimary ? primaryDataSelectIdNew : this.generateUUID();

			dsDataArray.some((dsData: DSData.RootTop) => {
				if (dsData.dsId === ds.id) {
					dsData.dsId = dsId;
					return true;
				}
				return false;
			});

			if (!isPrimary) {
				ds.updateFrom = primaryDataSelectIdNew;
			}
			ds.id = dsId;
			ds.blockId = blockId;
		});

		sample.id = blockId;
		sampledata.id = blockId;

		const block = this.mmBlocksService.createNewModel({
			id: blockId,
			dateRange: sampledata?.dateRange && copyObject(sampledata.dateRange),
		});

		this.storeService.pushPayload({ dataSelects, sample });
		this.tableBlock = block;
	}

	private generateUUID() {
		return -1 * Number(`${FakeModelIdPrefix.FORECASTS}${uniqueId()}`);
	}
}
